/**
 * Form-shaped adaptors over `@risksmart-app/scheduler-engine`'s RRULE subset.
 * The engine owns the grammar, the expansion and the clamp-not-skip behaviour;
 * nothing here re-implements any of it.
 */

import type {
  Frequency,
  ScheduleConfig,
  Window,
  WindowOverlap,
} from '@risksmart-app/scheduler-engine/src/index';
import {
  findWindowOverlap,
  MAX_INTERVAL,
  MAX_PREVIEW_WINDOWS,
  ONE_OFF_RRULE,
  previewWindows,
} from '@risksmart-app/scheduler-engine/src/index';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * The options the `Ends` radio offers, in the order it offers them. The single
 * enumeration of the set: the schema and the radio both read it, so neither can
 * drift from the other. `never` maps to no terminating RRULE component.
 */
export const ENDS = ['never', 'until', 'count'] as const;

export type RecurrenceEnds = (typeof ENDS)[number];

/**
 * The form's recurrence inputs. Numeric and date fields are strings because
 * they come straight from text/date inputs and are read while half-typed.
 */
export interface RecurrenceInput {
  Frequency: Frequency;
  Interval: string;
  Ends: RecurrenceEnds;
  Count: string;
  Until: string;
}

/** The recurrence inputs plus the seed dates, as the form holds them. */
export interface ScheduleWindowsInput extends RecurrenceInput {
  FirstStartDate: string;
  FirstDueDate: string;
  Repeat: boolean;
}

/** The same schedule in the shapes the engine takes. `null` rrule = one-off. */
export interface ScheduleWindows {
  FirstStartDate: Date;
  FirstDueDate: Date;
  rrule: string | null;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const parseWholeNumber = (value: string, min: number): number | null => {
  if (!/^\d+$/.test(value)) {
    return null;
  }

  const parsed = Number(value);

  return Number.isSafeInteger(parsed) && parsed >= min ? parsed : null;
};

/**
 * Normalise a date input to a calendar day at midnight UTC — the only date
 * shape the engine accepts. Returns null for empty or unparsable input rather
 * than throwing, so a half-typed date leaves the preview blank instead of
 * blowing up the form.
 */
export const toCalendarDayUtc = (value: string): Date | null => {
  if (value === '') {
    return null;
  }

  const parsed = dayjs.utc(value);
  if (!parsed.isValid()) {
    return null;
  }

  // dayjs parses non-strict and rolls overflow (Feb 30 → Mar 1). Round-trip the
  // date-only form so a non-existent day is rejected, not silently shifted.
  if (DATE_ONLY.test(value) && parsed.format('YYYY-MM-DD') !== value) {
    return null;
  }

  return parsed.startOf('day').toDate();
};

/**
 * Build an RRULE in the engine's subset: `FREQ=…;INTERVAL=n` plus at most one
 * of `COUNT=n` / `UNTIL=YYYYMMDD`. Returns null — never a partial rule, never a
 * throw — for any incomplete or out-of-range input, because a live preview
 * reads this on every keystroke.
 */
export const buildRRule = (input: RecurrenceInput): string | null => {
  const interval = parseWholeNumber(input.Interval, 1);
  if (interval === null || interval > MAX_INTERVAL) {
    return null;
  }

  const rule = `FREQ=${input.Frequency};INTERVAL=${interval}`;

  switch (input.Ends) {
    case 'count': {
      const count = parseWholeNumber(input.Count, 1);

      return count === null ? null : `${rule};COUNT=${count}`;
    }
    case 'until': {
      const until = toCalendarDayUtc(input.Until);

      return until === null
        ? null
        : `${rule};UNTIL=${dayjs.utc(until).format('YYYYMMDD')}`;
    }
    case 'never':
      return rule;
  }
};

/**
 * Resolve the seed dates and the rule together, or null while any of them is
 * still incomplete — a half-typed date, an interval mid-edit. This is the only
 * place that decides what "incomplete" means, so callers differ only in how
 * they react to it, and nothing downstream re-checks. A one-off carries no rule.
 */
export const resolveScheduleWindows = (
  input: ScheduleWindowsInput
): ScheduleWindows | null => {
  const FirstStartDate = toCalendarDayUtc(input.FirstStartDate);
  const FirstDueDate = toCalendarDayUtc(input.FirstDueDate);
  if (FirstStartDate === null || FirstDueDate === null) {
    return null;
  }

  if (!input.Repeat) {
    return { FirstStartDate, FirstDueDate, rrule: null };
  }

  const rrule = buildRRule(input);

  return rrule === null ? null : { FirstStartDate, FirstDueDate, rrule };
};

/** A one-off stores no rule, so the engine's sentinel stands in for it. */
const toScheduleConfig = ({
  FirstStartDate,
  FirstDueDate,
  rrule,
}: ScheduleWindows): ScheduleConfig => ({
  FirstStartDate,
  FirstDueDate,
  RRule: rrule ?? ONE_OFF_RRULE,
});

/**
 * The first `count` occurrence windows. The rule must be one `buildRRule`
 * produced — the engine throws on anything else, and that is a bug here rather
 * than a validation message.
 */
export const previewOccurrences = (
  windows: ScheduleWindows,
  count: number
): Window[] =>
  previewWindows(
    toScheduleConfig(windows),
    Math.min(count, MAX_PREVIEW_WINDOWS)
  );

/**
 * The first pair of consecutive windows that overlap — a window longer than its
 * own recurrence interval — or null if the schedule never overlaps.
 */
export const findOverlap = (windows: ScheduleWindows): WindowOverlap | null =>
  findWindowOverlap(toScheduleConfig(windows));
